import { type FormEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { z } from "zod";
import AppleLogo from "../../assets/images/apple_logo_black.svg";
import GoogleLogo from "../../assets/images/google_logo.svg";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import Loader from "../../components/Loader";
import ContentHeader from "../../components/app/headers/ContentHeader";
import DesktopAuthLayout from "../../components/layout/DesktopAuthLayout";
import { isAndroidApp } from "../../tools/android";
import { isIOSApp } from "../../tools/ios";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { gaEventAuthMethodSelected } from "../../tools/analytics/domains/onboarding.analytics";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useLoginPage } from "../../domains/login/hooks/useLoginPage";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { EmailOtpForm } from "../../components/auth/EmailOtpForm";
import LoginSheet from "../../components/signup/LoginSheet";
import { useSignupSheet } from "../../domains/auth/hooks/useSignupSheet";
import { useSignupOnboardingState } from "../../domains/auth/hooks/useSignupOnboardingState";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import {
  OTP_CODE_LENGTH,
  type EmailOtpState,
} from "../../domains/auth/hooks/useEmailOtp";

// Surfaces that bounce a visitor here because their address already has an
// account pass it along, so the login form opens pre-filled.
const locationStateSchema = z.object({
  email: z.string().nullable().default(null),
});

export default function LoginPage(): React.JSX.Element {
  const history = useHistory();
  const { email: prefilledEmail } = useLocationState(locationStateSchema);
  const { isLoading } = useCurrentUser();
  const { isInvitedToRoom, onboardingRoomData } = useSignupOnboardingState();
  const {
    email,
    isOAuthLoading,
    setEmail,
    handleOAuth,
    otpState,
    otpCooldownRemaining,
    code,
    setCode,
    handleRequestCode,
    handleSubmitCode,
    handleResendCode,
    handleChangeEmail,
  } = useLoginPage(prefilledEmail);

  const isNativeApp = isIOSApp() || isAndroidApp();

  // Already an account: the login form IS this page, so the address goes
  // straight into it instead of pushing `/login` onto `/login`.
  const fillLoginForm = useCallback(
    (existingEmail: string) => {
      setEmail(existingEmail);
    },
    [setEmail]
  );

  // Signing up is the same sheet the `/welcome` front door opens, over this
  // page. It used to be a link to `/signup`, a whole second page that asked the
  // same three questions this sheet asks.
  const signup = useSignupSheet({
    surface: "login_page",
    initiallyOpen: false,
    onExistingAccount: fillLoginForm,
    returnTo: null,
  });

  if (isLoading) {
    return <Loader expanded />;
  }

  const signupSheet = (
    <LoginSheet
      isOpen={signup.isOpen}
      onClose={signup.close}
      onOAuth={signup.handleOAuth}
      onEmailSubmit={signup.handleEmailSubmit}
      // Sheet dismissal is only reported from the `/welcome` front door, whose
      // funnel the event measures. Nothing to notify here.
      onEmailFirstTyped={null}
      onQRCodeScan={
        isNativeApp && !isInvitedToRoom
          ? () => {
              gaEventAuthMethodSelected({
                method: "qr_code",
                intent: "signup",
                surface: "login_page",
              });
              signup.close();
              history.push("/signup/qrcode");
            }
          : null
      }
      invitedRoomLogoUrl={
        onboardingRoomData !== null ? getRoomLogoUrl(onboardingRoomData) : null
      }
      // This page prints no legal line of its own, and the sheet is where the
      // account is created.
      displaysLegalNotice
      disabled={signup.isSigningIn}
    />
  );

  const content = (
    <LoginContent
      email={email}
      isOAuthLoading={isOAuthLoading}
      onEmailChange={setEmail}
      onSubmit={(e) => {
        e.preventDefault();
        void handleRequestCode();
      }}
      onOAuth={handleOAuth}
      otpState={otpState}
      otpCooldownRemaining={otpCooldownRemaining}
      code={code}
      onCodeChange={setCode}
      onCodeComplete={(submitted) => {
        void handleSubmitCode(submitted);
      }}
      onResendCode={handleResendCode}
      onChangeEmail={handleChangeEmail}
      onSignupClick={signup.open}
    />
  );

  if (isDesktopLayout()) {
    return (
      <DesktopAuthLayout>
        {content}
        {signupSheet}
      </DesktopAuthLayout>
    );
  }

  return (
    <div className="flex flex-col size-full bg-[#fbfbfb] md:items-center md:justify-center">
      <ContentHeader
        transparent
        // The WelcomePage is the front door, so Back returns there.
        backTo="/welcome"
        className="pt-(--safe-area-top) mt-3 md:hidden"
      />
      {/* Content */}
      <div className="flex flex-1 flex-col px-6 py-12 overflow-y-auto md:flex-initial md:w-full md:max-w-md md:rounded-2xl md:border md:border-ds-black/8 md:bg-white md:p-10 md:shadow-sm">
        {content}
      </div>
      {signupSheet}
    </div>
  );
}

// Login form content (title, OAuth buttons, email form), shared by the mobile
// page and the desktop split-screen shell.
function LoginContent({
  email,
  isOAuthLoading,
  onEmailChange,
  onSubmit,
  onOAuth,
  otpState,
  otpCooldownRemaining,
  code,
  onCodeChange,
  onCodeComplete,
  onResendCode,
  onChangeEmail,
  onSignupClick,
}: {
  email: string;
  isOAuthLoading: boolean;
  onEmailChange: (email: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onOAuth: (provider: "apple" | "google") => void;
  otpState: EmailOtpState;
  otpCooldownRemaining: number;
  code: string;
  onCodeChange: (code: string) => void;
  onCodeComplete: (code: string) => void;
  onResendCode: () => void;
  onChangeEmail: () => void;
  onSignupClick: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  // The address is the whole credential.
  const isFormValid = email.trim().length > 0 && email.includes("@");
  const isAwaitingCode = otpState.status !== "idle";
  const isOtpBusy =
    otpState.status === "sending" || otpState.status === "verifying";

  // The code screen replaces the whole form, SSO buttons included: someone who
  // has just been mailed a code is answering that question, not choosing a
  // provider.
  if (isAwaitingCode) {
    return (
      <>
        {/* The page's own title block, in the same shape as "Welcome back!"
            below. The form itself is headless, so the signup carousel can print
            its heading in its header band like every other slide. */}
        <div className="flex flex-col gap-3">
          <h1 className="font-dm-sans text-heading-display font-semibold text-ds-black">
            {t("Enter your code")}
          </h1>
          <p className="font-dm-sans text-ui-body text-ds-black/45">
            {t("We sent a 6-digit code to {{email}}. No password needed.", {
              email,
            })}
          </p>
        </div>
        <div className="mt-8">
          <EmailOtpForm
            code={code}
            error={otpState.status === "awaiting_code" ? otpState.error : null}
            isBusy={isOtpBusy}
            cooldownRemaining={otpCooldownRemaining}
            onCodeChange={onCodeChange}
            onComplete={onCodeComplete}
            onResend={onResendCode}
            onChangeEmail={onChangeEmail}
            // Mounted only while a code is awaited, so it is always on stage.
            autoFocus
          />
        </div>
        {/* The carousel submits through its footer band; this page has no
            footer, so the same action lives at the end of the column. */}
        <Button
          type="button"
          variant={code.length === OTP_CODE_LENGTH ? "primary" : undefined}
          size="lg"
          className="w-full mt-8"
          disabled={code.length !== OTP_CODE_LENGTH || isOtpBusy}
          onClick={() => {
            onCodeComplete(code);
          }}
        >
          {isOtpBusy ? t("Logging in...") : t("Continue")}
        </Button>
      </>
    );
  }

  return (
    <>
      {/* Title */}
      <div className="flex flex-col gap-3">
        <h1 className="font-dm-sans text-heading-display font-semibold text-ds-black">
          {t("Welcome back!")}
        </h1>
        <p className="font-dm-sans text-ui-body text-ds-black/45">
          {t("Log in to your Juisci account")}
        </p>
      </div>

      {/* Email first: the address is the door, the providers are the
          shortcut. Same order as the signup card. */}
      <form className="flex flex-col gap-3 mt-8" onSubmit={onSubmit}>
        <div className="flex flex-col gap-1">
          <label className="font-dm-sans text-ui-note text-ds-black">
            {t("Email address")}
          </label>
          <Input
            type="email"
            variant="ghost"
            className="border-ds-slate-300 bg-white rounded-xl"
            placeholder="example@juisci.com"
            value={email}
            onChange={(e) => {
              onEmailChange(e.target.value);
            }}
            autoComplete="email"
          />
        </div>

        <Button
          type="submit"
          variant={isFormValid ? "primary" : undefined}
          size="lg"
          className="w-full mt-3"
          disabled={!isFormValid || isOtpBusy}
        >
          {isOtpBusy ? t("Sending the code...") : t("Continue")}
        </Button>
      </form>

      {/* Divider */}
      <div className="flex items-center gap-1 my-6">
        <div className="flex-1 h-px bg-ds-black/18" />
        <span className="font-dm-sans text-ui-caption font-medium text-ds-black/45 px-1">
          {t("OR")}
        </span>
        <div className="flex-1 h-px bg-ds-black/18" />
      </div>

      {/* OAuth buttons */}
      <div className="flex flex-col gap-3">
        {!isAndroidApp() && (
          <Button
            variant="outline"
            size="lg"
            disabled={isOAuthLoading}
            onClick={() => {
              onOAuth("apple");
            }}
          >
            <img className="h-4.5" src={AppleLogo} alt="" />
            {t("Continue with Apple")}
          </Button>
        )}
        <Button
          variant="outline"
          size="lg"
          disabled={isOAuthLoading}
          onClick={() => {
            onOAuth("google");
          }}
        >
          <img className="h-4.5" src={GoogleLogo} alt="" />
          {t("Continue with Google")}
        </Button>
      </div>

      {/* Signup — the same sheet the `/welcome` front door opens, over this
          page. There is no signup PAGE any more: it asked the same three
          questions the sheet asks. */}
      <div className="text-center pt-6">
        <span className="font-dm-sans text-ui-highlight text-ds-black">
          {t("Don't have an account?")}{" "}
          <button
            type="button"
            className="font-dm-sans text-ui-highlight font-semibold text-ds-mint-800 underline"
            onClick={onSignupClick}
          >
            {t("Sign up")}
          </button>
        </span>
      </div>
    </>
  );
}
